import {
  bpsToPercent,
  formatPercentToDecimalPlaces,
  getDiPricingInfo,
} from "@frec-js/common";
import { DirectIndexType, PricingPageAnchor } from "@frec-js/common-client";
import { motion, useScroll } from "framer-motion";
import { FC, PropsWithChildren, useRef } from "react";

import { FootnoteAnchor, PricingFootnotes } from "../../components/Anchor";
import { Button } from "../../components/Button";
import { Footnote } from "../../components/Link";
import { useIsMobile } from "../../hooks/useIsMobile";
import FeesImage from "../../public/webstatic/svg/fees.svg";
import { FadeInUp } from "../../utils/animations";
import { BrokerageWebPath } from "../../utils/paths";

export const PricingFees: FC<{ id?: string }> = ({
  id = PricingPageAnchor.Comparison,
}) => {
  const feeFillRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const { scrollYProgress } = useScroll({
    target: feeFillRef,
    // https://www.framer.com/motion/use-scroll/##scroll-offsets
    offset: ["end end", "start 0.1"],
  });

  return (
    <section
      id={id}
      className="bg-frecBeige grid grid-cols-[auto_minmax(0,86rem)_auto] lg:grid-rows-[auto_1px_auto_1px_auto_auto]"
    >
      {/* row */}
      <div className="">{/* empty */}</div>
      <div
        ref={feeFillRef}
        className="dotted-v flex flex-col items-center gap-8 py-16 lg:mx-12 lg:border-x"
      >
        <h4 className="text-center text-[32px]">Start direct indexing for</h4>
        <div className="flex flex-col items-center gap-4">
          <div className="lg:text[128px] relative px-2 text-[96px] font-medium">
            <motion.div
              className="bg-frecNeon absolute top-0 right-0 bottom-0 left-0 origin-left"
              initial={false}
              style={{
                scaleX: scrollYProgress,
              }}
            />
            <div className="relative z-10">
              {formatPercentToDecimalPlaces(
                bpsToPercent(
                  getDiPricingInfo(DirectIndexType.Sp500).directIndexBaseBPS, // using SP500 for the default case
                ),
                2,
              )}
            </div>
          </div>
          <div className="text-20 text-frecBlack/60 max-w-sm text-center">
            Annually
          </div>
        </div>
      </div>
      <div className="">{/* empty */}</div>
      {/* row */}
      <div className="dotted-h col-span-3 border-b">{/* empty */}</div>
      {/* row */}
      <div className="">{/* empty */}</div>
      <div className="dotted-v grid h-full items-center lg:mx-12 lg:grid-cols-[1fr_1fr] lg:border-x">
        <div className="dotted-v flex h-full flex-col justify-center gap-9 p-6 lg:border-r lg:p-16">
          <h2 className="h2">Get more for less</h2>
          <FadeInUp>
            <FeesImage alt="Visual comparison of fees" className="w-full" />
          </FadeInUp>
          <div>
            <p className="text-frecDarkGray text-[14px]">
              * Frec DI fees range from 0.10%-0.45% depending on strategy.
            </p>
            <p className="text-frecDarkGray text-[14px]">
              + Wealthfront also holds ETFs in their strategy, which incur
              additional expenses.
            </p>
          </div>
        </div>
        <div className="dotted-h grid grid-rows-[1fr_auto_1fr_auto_1fr] border-t lg:border-t-0">
          <GetMoreForLessRow title="Our pricing, compared">
            Direct indexing solutions from other providers are more costly than
            most of our indices, with Wealthfront charging 0.25%, traditional
            brokerages 0.40%, and wealth advisors up to 1.0%.
            <Footnote anchor={FootnoteAnchor.LowerFeesDisclaimer}>
              <sup>{PricingFootnotes.withLowerFeesDisclaimer}</sup>
            </Footnote>
          </GetMoreForLessRow>
          <GetMoreForLessRow title="Commitment to keep costs low">
            Investing in ETFs with robo-advisors like Betterment means paying
            management fees on top of ETF expenses. With Frec, there are no ETF
            expenses, so most of our index strategies cost less. Enjoy smarter
            passive investing without extra fees cutting into your tax savings.
          </GetMoreForLessRow>
          <div className="px-6 py-6 lg:px-12 lg:py-7">
            <Button
              variant="blueoutline"
              small={isMobile}
              onClick={() => (location.href = BrokerageWebPath.signup)}
            >
              Get started
            </Button>
          </div>
        </div>
      </div>
      <div className="">{/* empty */}</div>
      {/* row */}
      <div className="dotted-h col-span-3 border-b">{/* empty */}</div>
    </section>
  );
};

const GetMoreForLessRow: FC<PropsWithChildren<{ title: string }>> = ({
  title,
  children,
}) => {
  return (
    <>
      <h3 className="text-20 dotted-h flex items-center border-b px-6 py-2 lg:px-12 lg:py-7 lg:text-[28px]">
        {title}
      </h3>
      <p className="lg:text-20 dotted-h border-b px-6 py-6 lg:px-12 lg:py-7">
        {children}
      </p>
    </>
  );
};
